import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import { MapPin, Link as LinkIcon, Award, Globe, DollarSign, Upload, Plus, X } from 'lucide-react';

export default function ProfileEnhanced() {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    location: '',
    bio: '',
    availability_status: 'open',
    salary_min: '',
    salary_max: '',
    portfolio_url: '',
    social_links: { linkedin: '', github: '', twitter: '', website: '' },
    certifications: [],
    languages: [],
    education: [],
    experience: []
  });

  useEffect(() => {
    if (user && token) {
      const fetchProfile = async () => {
        try {
          setLoading(true);
          const res = await api.get(`/api/users/${user.id}`);
          const data = res.data || {};
          
          setProfile({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            position: data.position || '',
            location: data.location || '',
            bio: data.bio || '',
            availability_status: data.availability_status || 'open',
            salary_min: data.salary_min || '',
            salary_max: data.salary_max || '',
            portfolio_url: data.portfolio_url || '',
            social_links: data.social_links || { linkedin: '', github: '', twitter: '', website: '' },
            certifications: data.certifications || [],
            languages: data.languages || [],
            education: [],
            experience: []
          });
          
          if (data.avatar_url) setAvatarPreview(data.avatar_url);
        } catch (err) {
          console.error('Failed to load profile:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchProfile();
    }
  }, [user, token]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user || !token) {
      setError('You must be logged in to save your profile');
      return;
    }

    try {
      setLoading(true);
      await api.put(`/api/users/${user.id}`, {
        name: profile.name,
        position: profile.position,
        phone: profile.phone,
        location: profile.location,
        bio: profile.bio,
        availability_status: profile.availability_status,
        salary_min: profile.salary_min ? parseInt(profile.salary_min) : null,
        salary_max: profile.salary_max ? parseInt(profile.salary_max) : null,
        portfolio_url: profile.portfolio_url,
        social_links: profile.social_links,
        certifications: profile.certifications,
        languages: profile.languages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file || !user || !token) return;
    const formData = new FormData();
    formData.append('avatar', file);
    
    try {
      setLoading(true);
      const res = await api.post(`/api/users/${user.id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.data?.url) {
        setAvatarPreview(res.data.url);
        setSuccess('Avatar uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to upload avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
    uploadAvatar(file);
  };

  const addCertification = () => {
    setProfile(prev => ({
      ...prev,
      certifications: [...prev.certifications, { id: Date.now(), name: '', issuer: '', year: '' }]
    }));
  };

  const removeCertification = (id) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c.id !== id)
    }));
  };

  const updateCertification = (id, field, value) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      )
    }));
  };

  const addLanguage = () => {
    setProfile(prev => ({
      ...prev,
      languages: [...prev.languages, { id: Date.now(), name: '', proficiency: 'Intermediate' }]
    }));
  };

  const removeLanguage = (id) => {
    setProfile(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l.id !== id)
    }));
  };

  const updateLanguage = (id, field, value) => {
    setProfile(prev => ({
      ...prev,
      languages: prev.languages.map(l =>
        l.id === id ? { ...l, [field]: value } : l
      )
    }));
  };

  const completeness = () => {
    const fields = [
      profile.name, profile.phone, profile.location, profile.bio,
      profile.social_links.linkedin, profile.certifications.length > 0,
      profile.languages.length > 0, avatarPreview
    ];
    const filled = fields.filter(Boolean).length;
    return Math.round((filled / fields.length) * 100);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Profile Completeness */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">Profile Completeness</span>
            <span className="text-blue-600 font-bold">{completeness()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${completeness()}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          <h1 className="text-3xl font-bold mb-6">My Profile</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-800">
              {success}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl text-gray-500">
                    {profile.name ? profile.name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'U'}
                  </span>
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <Upload size={18} />
                  Upload Photo
                </button> • Optional
                <p className="text-sm text-gray-500 mt-2">JPG, PNG or GIF • Max 5MB</p>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Position / Title</label>
                <input
                  type="text"
                  value={profile.position}
                  onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MapPin size={16} />
                  Location
                </label>
                <input
                  type="text"
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City, Country"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Professional Summary</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tell employers about yourself..."
              />
            </div>

            {/* Availability & Salary */}
            {user?.user_type === 'job_seeker' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                  <select
                    value={profile.availability_status}
                    onChange={(e) => setProfile({ ...profile, availability_status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="open">Actively Looking</option>
                    <option value="passive">Open to Offers</option>
                    <option value="not_available">Not Available</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <DollarSign size={16} />
                    Min Salary
                  </label>
                  <input
                    type="number"
                    value={profile.salary_min}
                    onChange={(e) => setProfile({ ...profile, salary_min: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Salary</label>
                  <input
                    type="number"
                    value={profile.salary_max}
                    onChange={(e) => setProfile({ ...profile, salary_max: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="80000"
                  />
                </div>
              </div>
            )}

            {/* Social Links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <LinkIcon size={16} />
                Social Links
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="url"
                  value={profile.social_links.linkedin}
                  onChange={(e) => setProfile({
                    ...profile,
                    social_links: { ...profile.social_links, linkedin: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="LinkedIn URL"
                />
                <input
                  type="url"
                  value={profile.social_links.github}
                  onChange={(e) => setProfile({
                    ...profile,
                    social_links: { ...profile.social_links, github: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="GitHub URL"
                />
                <input
                  type="url"
                  value={profile.social_links.twitter}
                  onChange={(e) => setProfile({
                    ...profile,
                    social_links: { ...profile.social_links, twitter: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Twitter URL"
                />
                <input
                  type="url"
                  value={profile.social_links.website}
                  onChange={(e) => setProfile({
                    ...profile,
                    social_links: { ...profile.social_links, website: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Personal Website"
                />
              </div>
            </div>

            {/* Portfolio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio URL</label>
              <input
                type="url"
                value={profile.portfolio_url}
                onChange={(e) => setProfile({ ...profile, portfolio_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://myportfolio.com"
              />
            </div>

            {/* Certifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Award size={16} />
                Certifications
              </label>
              <div className="space-y-3">
                {profile.certifications.map((cert) => (
                  <div key={cert.id} className="flex gap-3 items-start">
                    <input
                      type="text"
                      value={cert.name}
                      onChange={(e) => updateCertification(cert.id, 'name', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Certification Name"
                    />
                    <input
                      type="text"
                      value={cert.issuer}
                      onChange={(e) => updateCertification(cert.id, 'issuer', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Issuer"
                    />
                    <input
                      type="text"
                      value={cert.year}
                      onChange={(e) => updateCertification(cert.id, 'year', e.target.value)}
                      className="w-24 px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Year"
                    />
                    <button
                      type="button"
                      onClick={() => removeCertification(cert.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCertification}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <Plus size={18} />
                  Add Certification
                </button>
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Globe size={16} />
                Languages
              </label>
              <div className="space-y-3">
                {profile.languages.map((lang) => (
                  <div key={lang.id} className="flex gap-3 items-center">
                    <input
                      type="text"
                      value={lang.name}
                      onChange={(e) => updateLanguage(lang.id, 'name', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Language"
                    />
                    <select
                      value={lang.proficiency}
                      onChange={(e) => updateLanguage(lang.id, 'proficiency', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Basic">Basic</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Native">Native</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeLanguage(lang.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLanguage}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <Plus size={18} />
                  Add Language
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
